import { Check } from "lucide-react";
import { useStoredState } from "@/hooks/useStoredState";
import { ToolShell } from "@/components/tools/ToolShell";
import { AutoTextarea, Pills, RAG_OPTIONS, Stat } from "@/components/tools/fields";
import { SubHead } from "@/components/tools/blocks";

const EI_REVIEW = ["Assessments used appropriately", "Concerns identified early", "Data reviewed regularly", "Parent concerns considered", "Staff know referral processes"];
const EI_TEXT = [
  { k: "evidence", label: "Evidence" },
  { k: "strengths", label: "Strengths" },
  { k: "development", label: "Areas for Development" },
  { k: "actions", label: "Actions" },
];
const AREAS = ["Leadership & Governance", "Quality First Teaching", "Graduated Approach", "SEND Register Management", "SEN Support", "EHCP Provision", "Attendance", "Behaviour", "Inclusion", "Transition", "Parent Engagement", "Outcomes Monitoring", "Action Planning"]
  .map((t) => ({ id: t.toLowerCase().replace(/[^a-z]+/g, "-"), title: t }));

export const TickList = ({ name, items, value = {}, onChange }) => (
  <ul className="grid gap-2 sm:grid-cols-2">
    {items.map((t, i) => (
      <li key={t}>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border-2 border-line bg-surface p-3" data-testid={`${name}-${i}`}>
          <input type="checkbox" checked={!!value[i]} onChange={(e) => onChange({ ...value, [i]: e.target.checked })} className="peer sr-only" />
          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 ${value[i] ? "border-brand bg-brand text-brand-fg" : "border-ink-soft"}`} aria-hidden="true">
            {value[i] && <Check size={14} strokeWidth={3} />}
          </span>
          <span className="font-bold">{t}</span>
        </label>
      </li>
    ))}
  </ul>
);

export const ProvisionReview = () => {
  const [data, setData, reset] = useStoredState("provision-audit-v2", { ei: { ticks: {} }, areas: {} });
  const ei = data.ei || {};
  const setEi = (k, v) => setData((d) => ({ ...d, ei: { ...d.ei, [k]: v } }));
  const setArea = (id, patch) => setData((d) => ({ ...d, areas: { ...d.areas, [id]: { ...(d.areas?.[id] || {}), ...patch } } }));
  const all = [ei.rag, ...AREAS.map((a) => data.areas?.[a.id]?.rag)];
  const count = (v) => all.filter((r) => r === v).length;
  return (
    <ToolShell id="provision-audit" onReset={reset}>
      <SubHead letter="A" title="Early Identification" />
      <Pills legend="RAG status for Early Identification" hideLegend={false} name="pa-ei" options={RAG_OPTIONS} value={ei.rag} onChange={(v) => setEi("rag", v)} testid="pa-ei" />
      <h5 className="mb-3 mt-6 font-bold">Review areas</h5>
      <TickList name="pa-ei-review" items={EI_REVIEW} value={ei.ticks} onChange={(v) => setEi("ticks", v)} />
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {EI_TEXT.map((f) => <AutoTextarea key={f.k} id={`pa-ei-${f.k}`} label={f.label} rows={3} value={ei[f.k]} onChange={(v) => setEi(f.k, v)} testid={`pa-ei-${f.k}`} />)}
      </div>

      <SubHead letter="B" title="Other areas of provision" />
      <ol className="divide-y divide-line border-b border-line">
        {AREAS.map((a) => (
          <li key={a.id} className="avoid-break grid gap-4 py-5 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
            <h5 className="text-lg font-bold">{a.title}</h5>
            <div className="space-y-3">
              <Pills legend={`RAG status for ${a.title}`} name={`pa-${a.id}`} options={RAG_OPTIONS} value={data.areas?.[a.id]?.rag} onChange={(v) => setArea(a.id, { rag: v })} testid={`pa-${a.id}`} />
              <AutoTextarea id={`pa-${a.id}-notes`} label={`Notes for ${a.title}`} hideLabel placeholder="Notes" value={data.areas?.[a.id]?.notes} onChange={(v) => setArea(a.id, { notes: v })} testid={`pa-${a.id}-notes`} />
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" aria-live="polite">
        <Stat label="Red" value={count("red")} tone="red" testid="pa-count-red" />
        <Stat label="Amber" value={count("amber")} tone="amber" testid="pa-count-amber" />
        <Stat label="Green" value={count("green")} tone="green" testid="pa-count-green" />
        <Stat label="Areas rated" value={`${all.filter(Boolean).length}/${all.length}`} testid="pa-count-rated" />
      </div>
    </ToolShell>
  );
};
